"use client";
import React, { useRef } from 'react';
import Link from 'next/link';
import { StrapiArticleInterface } from '@/types/strapi-collections';
import { StrapiImage } from "@/comp/_Utility/StrapiImage";
import { ColorScheme } from "./C-Article-Grid";

const NEWS_BASE_URL = process.env.NEXT_PUBLIC_NEWS_URL || "uudised";

if(!NEWS_BASE_URL){
    throw new Error("News URL not defined")
}

interface Props {
    articles: StrapiArticleInterface[];
    colorPalette: ColorScheme[];
}

export default function ArticleCarousel({ articles, colorPalette }: Props) {
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const scroll = (direction: "left" | "right") => {
        if (!scrollContainerRef.current) return;
        const scrollAmount = 340;
        scrollContainerRef.current.scrollBy({
            left: direction === "left" ? -scrollAmount : scrollAmount,
            behavior: "smooth"
        });
    };

    return (
        <div className="relative w-full group">
            {/* Scroll Navigation Buttons */}
            <button
                onClick={() => scroll("left")}
                className="absolute -left-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-white/80 text-gray-900 shadow-md backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                aria-label="Keri vasakule"
            >
                ◀
            </button>
            <button
                onClick={() => scroll("right")}
                className="absolute -right-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-white/80 text-gray-900 shadow-md backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                aria-label="Keri paremale"
            >
                ▶
            </button>

            {/* Swipable Track */}
            <div
                ref={scrollContainerRef}
                className="flex gap-6 overflow-x-auto scrollbar-none snap-x snap-mandatory py-4 px-1"
                style={{ scrollbarWidth: "none" }}
            >
                {articles.map((article, index) => {
                    const color = colorPalette[index % colorPalette.length];
                    const hasThumbnail = Boolean(article.thumbnail?.url);
                    const formattedDate = article.publishDate
                        ? new Date(article.publishDate).toLocaleDateString("et-EE", {
                            day: "numeric",
                            month: "short",
                            year: "numeric"
                        })
                        : null;
                    const articleLink = `/${NEWS_BASE_URL.replace(/^\//, "")}/${article.documentId}`;

                    return (
                        <div key={article.id} className="snap-start flex-shrink-0 w-[280px] sm:w-[320px] lg:w-[340px]">
                            <Link
                                href={articleLink}
                                className={`group relative overflow-hidden rounded-2xl shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg flex flex-col h-[340px] ${color.bg} ${color.text}`}
                            >
                                {hasThumbnail && (
                                    <div className="relative w-full h-44 overflow-hidden bg-gray-900 flex-shrink-0">
                                        <StrapiImage
                                            media={article.thumbnail}
                                            quality="medium"
                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                        />
                                    </div>
                                )}

                                <div className="p-5 flex flex-col flex-1 justify-between gap-3">
                                    <div className="flex flex-col gap-1.5">
                                        {formattedDate && (
                                            <time className="text-xs font-semibold uppercase tracking-wider opacity-75">
                                                {formattedDate}
                                            </time>
                                        )}
                                        <h3 className="font-bold text-lg tracking-tight line-clamp-3">
                                            {article.title}
                                        </h3>
                                    </div>

                                    <span className="inline-flex items-center text-xs font-bold uppercase tracking-widest opacity-80 group-hover:translate-x-1 transition-transform mt-auto">
                                        Loe lähemalt →
                                    </span>
                                </div>
                            </Link>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
